/**
 * NemoValidator - A lightweight class-based form validation library
 * @module nemo-validator
 */

/**
 * Form validation class that handles input validation based on CSS classes
 */
class NemoValidator {
  /**
   * Creates a new validator instance
   * @param {string} formClass - CSS class that identifies forms to validate
   * @param {Object} customRules - Optional custom validation rules to extend default rules
   */
  constructor(formClass, customRules = {}) {
    this.formClass = formClass;

    this.validationRules = {
      'validate-name': {
        pattern: /^[a-zA-Z .]+$/,
        message: 'Only alphabets and dots are allowed.',
        filterPattern: /[^a-zA-Z .]/g
      },
      'validate-mobile': {
        pattern: /^\d{10}$/,
        message: 'Please enter a valid 10-digit mobile number.',
        filterPattern: /[^\d]/g,
        maxLength: 10
      },
      'validate-aadhar': {
        pattern: /^\d{12}$/,
        message: 'Please enter a valid 12-digit Aadhar number.',
        filterPattern: /[^\d]/g,
        maxLength: 12
      },
      'validate-pan': {
        pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
        message: 'Please enter a valid PAN number (AYTPD0038F format).',
        filterPattern: /[^a-zA-Z0-9]/g,
        maxLength: 10
      },
      'validate-email': {
        pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: 'Please enter a valid email address.',
        filterPattern: /[^a-zA-Z0-9.@_-]/g
      },
      'validate-address': {
        pattern: /^[a-zA-Z0-9 .,/]+$/,
        message: 'Only alphabets, numbers, dots, forward slash and spaces are allowed.',
        filterPattern: /[^a-zA-Z0-9 .,/]/g
      },
      'validate-pin': {
        pattern: /^\d{6}$/,
        message: 'Please enter a valid 6-digit Pin Code.',
        filterPattern: /[^\d]/g,
        maxLength: 6
      },
      'validate-text': {
        pattern: /^[a-zA-Z .,/]+$/,
        message: 'Only alphabets, dots, forward slash and spaces are allowed.',
        filterPattern: /[^a-zA-Z .,/]/g
      },
      'validate-numeric': {
        pattern: /^\d+$/,
        message: 'Only numbers are allowed.',
        filterPattern: /[^\d]/g
      },
      'validate-business': {
        pattern: /^[a-zA-Z0-9 .,/]+$/,
        message: 'Only alphabets, numbers, dots, forward slash and spaces are allowed.',
        filterPattern: /[^a-zA-Z0-9 .,/]/g
      },
      'validate-other': {
        pattern: /^[a-zA-Z0-9 .,/]+$/,
        message: 'Only alphabets, numbers, dots, forward slash and spaces are allowed.',
        filterPattern: /[^a-zA-Z0-9 .,/]/g
      },
      'validate-gst': {
        pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
        message: 'Please enter a valid 15-digit GSTIN number (22AAAAA0000A1Z5).',
        filterPattern: /[^a-zA-Z0-9]/g,
        maxLength: 15
      },
      'validate-uam': {
        pattern: /^[A-Z]{2}[0-9]{2}[A-Z]{1}[0-9]{7}$/,
        message: 'Please enter a valid UAM number (DL05A1234567 format).',
        filterPattern: /[^a-zA-Z0-9]/g,
        maxLength: 12
      },
      'validate-udyam': {
        pattern: /^[A-Z]{5}-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/,
        message: 'Please enter a valid 16 characters UDYAM number. (i.e UDYAM-XX-00-0000000)',
        filterPattern: /[^A-Z0-9-]/g,
        maxLength: 16
      },
      'validate-amount': {
        pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
        message: 'Enter amount up to 2 decimal places',
        filterPattern: /[^0-9.]/g
      },
      'validate-two-decimal': {
        pattern: /^[0-9]+(\.[0-9]{0,2})?$/,
        message: 'Please enter a valid number with up to 2 decimal places',
        filterPattern: /[^0-9.]/g,
        setupEvents: function (element) {

          let lastValidValue = '';

          const isValidDecimalFormat = (value) => {
            if (!value) return true;
            return /^[0-9]+(\.[0-9]{0,2})?$/.test(value);
          };


          element.addEventListener('keydown', function (e) {

            if (e.key === 'Backspace' || e.key === 'Delete' ||
              e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
              e.key === 'Tab' || e.key === 'Home' || e.key === 'End' ||
              e.ctrlKey || e.altKey || e.metaKey) {
              return;
            }


            const value = this.value;
            const start = this.selectionStart;
            const end = this.selectionEnd;


            const potentialNewValue = value.substring(0, start) + e.key + value.substring(end);


            if (!isValidDecimalFormat(potentialNewValue)) {
              e.preventDefault();
              return false;
            }
          });


          element.addEventListener('input', function () {
            if (isValidDecimalFormat(this.value)) {
              lastValidValue = this.value;
            } else {

              this.value = lastValidValue;
            }


            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });


          element.addEventListener('paste', function (e) {

            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            const start = this.selectionStart;
            const end = this.selectionEnd;


            const potentialNewValue = this.value.substring(0, start) + pastedText + this.value.substring(end);


            if (!isValidDecimalFormat(potentialNewValue)) {
              e.preventDefault();


              if (potentialNewValue.includes('.')) {
                const parts = potentialNewValue.split('.');
                if (parts.length === 2) {
                  const correctedValue = parts[0] + '.' + parts[1].substring(0, 2);


                  if (isValidDecimalFormat(correctedValue)) {
                    this.value = correctedValue;

                    this.selectionStart = this.selectionEnd =
                      Math.min(correctedValue.length, start + correctedValue.length - potentialNewValue.length);


                    this.dispatchEvent(new Event('input', { bubbles: true }));
                  }
                }
              }
            }
          });


          element.addEventListener('focus', function () {
            if (isValidDecimalFormat(this.value)) {
              lastValidValue = this.value;
            }
          });
        }
      },


      'validate-strong-password': {
        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        message: 'Password must contain at least 8 characters, including uppercase, lowercase, number and special character.',
        setupEvents: function (element) {
          element.addEventListener('input', function () {

            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });
        }
      },


      'validate-age': {
        pattern: /^(1[8-9]|[2-9]\d|100)$/,
        message: 'Age must be between 18 and 100.',
        filterPattern: /[^\d]/g,
        maxLength: 3,
        setupEvents: function (element) {
          element.addEventListener('input', function () {

            this.value = this.value.replace(/[^\d]/g, '');


            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });


          element.addEventListener('keydown', function (e) {

            if (e.ctrlKey || e.altKey || e.metaKey ||
              e.key === 'Backspace' || e.key === 'Delete' ||
              e.key === 'Tab' || e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight' || e.key === 'Home' ||
              e.key === 'End') {
              return;
            }


            if (!/^\d$/.test(e.key)) {
              e.preventDefault();
            }
          });
        },

        customValidation: function (value) {
          if (!value) return false;

          const age = parseInt(value);


          if (age < 18) {
            this.message = 'Age must be at least 18.';
            return false;
          }


          if (age > 100) {
            this.message = 'Age must be at most 100.';
            return false;
          }


          this.message = 'Age must be between 18 and 100.';
          return true;
        }
      },


      'validate-credit-card': {
        pattern: /^\d{13,16}$|^(?:\d{4}[ -]){3}\d{4}$|^(?:\d{4}[ -]){2}\d{4}[ -]\d{4}$/,
        message: 'Please enter a valid credit card number.',
        filterPattern: /[^\d\s-]/g,
        maxLength: 23, // Accounts for spaces/dashes (16 digits + 4 spaces)
        setupEvents: function (element) {

          const formatCreditCard = (value) => {

            value = value.replace(/\D/g, '');


            if (value.length > 16) {
              value = value.substring(0, 16);
            }

            if (value.length === 0) return '';


            let result = '';
            for (let i = 0; i < value.length; i++) {

              if (i > 0 && i % 4 === 0) {
                result += ' ';
              }
              result += value.charAt(i);
            }
            return result;
          };


          if (element.value) {
            element.value = formatCreditCard(element.value);
          }


          element.addEventListener('input', function () {
            const cursorPos = this.selectionStart;


            const rawValue = this.value.replace(/\s/g, '');
            const valueBeforeCursor = this.value.substring(0, cursorPos);
            const digitsBeforeCursor = valueBeforeCursor.replace(/\s/g, '').length;


            this.value = formatCreditCard(rawValue);


            const newSpacesBeforeCursor = Math.floor(digitsBeforeCursor / 4);
            const newCursorPos = digitsBeforeCursor + newSpacesBeforeCursor;


            this.selectionStart = this.selectionEnd = Math.min(newCursorPos, this.value.length);


            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });


          element.addEventListener('paste', function () {

            setTimeout(() => {
              this.value = formatCreditCard(this.value);

              this.selectionStart = this.selectionEnd = this.value.length;

              const validator = window.nemoValidatorInstance;
              if (validator) {
                validator.validateField(this);
              }
            }, 0);
          });


          element.addEventListener('keydown', function (e) {

            if (e.ctrlKey || e.altKey || e.metaKey ||
              e.key === 'Backspace' || e.key === 'Delete' ||
              e.key === 'Tab' || e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight' || e.key === 'Home' ||
              e.key === 'End' || e.key === ' ') {
              return;
            }


            if (!/^\d$/.test(e.key)) {
              e.preventDefault();
            }
          });
        },

        customValidation: function (value) {
          if (!value) return false;


          value = value.replace(/[\s-]/g, '');


          if (value.length < 13) {
            this.message = 'Credit card number is too short.';
            return false;
          }


          if (value.length > 16) {
            this.message = 'Credit card number is too long.';
            return false;
          }


          if (!/^\d+$/.test(value)) {
            this.message = 'Credit card number can only contain digits.';
            return false;
          }


          let sum = 0;
          let shouldDouble = false;


          for (let i = value.length - 1; i >= 0; i--) {
            let digit = parseInt(value.charAt(i));

            if (shouldDouble) {
              digit *= 2;
              if (digit > 9) digit -= 9;
            }

            sum += digit;
            shouldDouble = !shouldDouble;
          }

          const isValid = (sum % 10) === 0;
          if (!isValid) {
            this.message = 'Invalid credit card number.';
          } else {
            this.message = 'Please enter a valid credit card number.';
          }

          return isValid;
        }
      },


      'validate-current-date': {
        message: 'Only the current date is allowed.',
        setupEvents: function (element) {

          const getTodayString = () => {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };


          const todayString = getTodayString();
          element.value = todayString;
          element.min = todayString;
          element.max = todayString;


          element.addEventListener('input', function () {
            if (this.value !== todayString) {
              this.value = todayString;
            }
          });


          element.addEventListener('keydown', function (e) {

            if (e.key !== 'Tab') {
              e.preventDefault();
            }
          });


          element.addEventListener('change', function () {
            this.value = todayString;
          });
        },
        customValidation: function (value) {

          const today = new Date();
          today.setHours(0, 0, 0, 0);


          const parts = value.split('-');
          if (parts.length !== 3) return false;

          const inputDate = new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2])
          );
          inputDate.setHours(0, 0, 0, 0);


          return inputDate.getTime() === today.getTime();
        }
      },

      // NEW FEATURES - Additional validation rules
      'validate-url': {
        pattern: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
        message: 'Please enter a valid URL.',
        filterPattern: /[^a-zA-Z0-9._~:/?#[\]@!$&'()*+,;=%-]/g
      },

      'validate-phone-international': {
        pattern: /^\+[1-9]\d{6,14}$/,
        message: 'Please enter a valid international phone number (e.g., +1234567890).',
        filterPattern: /[^\d+]/g,
        maxLength: 16,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            if (this.value && !this.value.startsWith('+')) {
              this.value = '+' + this.value.replace(/[^\d]/g, '');
            }
          });
        }
      },

      'validate-username': {
        pattern: /^[a-zA-Z0-9_]{3,20}$/,
        message: 'Username must be 3-20 characters long and contain only letters, numbers, and underscores.',
        filterPattern: /[^a-zA-Z0-9_]/g,
        maxLength: 20
      },

      'validate-postal-code': {
        pattern: /^[A-Z0-9]{3,10}$/,
        message: 'Please enter a valid postal code.',
        filterPattern: /[^a-zA-Z0-9]/g,
        maxLength: 10,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            this.value = this.value.toUpperCase();
          });
        }
      },

      'validate-date-future': {
        message: 'Date must be in the future.',
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });

          element.addEventListener('change', function () {
            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });
        },
        customValidation: function (value) {
          if (!value) return false;

          const inputDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          return inputDate > today;
        }
      },

      'validate-date-past': {
        message: 'Date must be in the past.',
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });

          element.addEventListener('change', function () {
            const validator = window.nemoValidatorInstance;
            if (validator) {
              validator.validateField(this);
            }
          });
        },
        customValidation: function (value) {
          if (!value) return false;

          const inputDate = new Date(value);
          const today = new Date();
          today.setHours(23, 59, 59, 999);

          return inputDate < today;
        }
      },

      'validate-time': {
        pattern: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
        message: 'Please enter a valid time in HH:MM format.',
        filterPattern: /[^0-9:]/g,
        maxLength: 5,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            let value = this.value.replace(/[^0-9]/g, '');
            if (value.length >= 2) {
              value = value.substring(0, 2) + ':' + value.substring(2, 4);
            }
            this.value = value;
          });
        }
      },

      'validate-color-hex': {
        pattern: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
        message: 'Please enter a valid hex color code (e.g., #FF0000 or #F00).',
        filterPattern: /[^#A-Fa-f0-9]/g,
        maxLength: 7,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            if (this.value && !this.value.startsWith('#')) {
              this.value = '#' + this.value.replace(/[^A-Fa-f0-9]/g, '');
            }
            this.value = this.value.toUpperCase();
          });
        }
      },

      'validate-percentage': {
        pattern: /^(100(\.0{1,2})?|[1-9]?\d(\.\d{1,2})?)$/,
        message: 'Please enter a valid percentage (0-100).',
        filterPattern: /[^0-9.]/g,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            const value = parseFloat(this.value);
            if (value > 100) {
              this.value = '100';
            }
          });
        }
      },

      'validate-ip-address': {
        pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
        message: 'Please enter a valid IP address (e.g., 192.168.1.1).',
        filterPattern: /[^0-9.]/g,
        maxLength: 15
      },

      'validate-mac-address': {
        pattern: /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/,
        message: 'Please enter a valid MAC address (e.g., 00:1B:44:11:3A:B7).',
        filterPattern: /[^0-9A-Fa-f:-]/g,
        maxLength: 17,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            let value = this.value.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
            let formatted = '';
            for (let i = 0; i < value.length && i < 12; i++) {
              if (i > 0 && i % 2 === 0) {
                formatted += ':';
              }
              formatted += value[i];
            }
            this.value = formatted;
          });
        }
      },

      'validate-slug': {
        pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
        message: 'Please enter a valid slug (lowercase letters, numbers, and hyphens only).',
        filterPattern: /[^a-z0-9-]/g,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            this.value = this.value.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/--+/g, '-');
          });
        }
      },

      'validate-currency': {
        // Dynamic pattern - will be set by customValidation
        pattern: null,
        message: 'Please enter a valid currency amount.',
        
        // Custom validation that handles different currency formats
        customValidation: function(value, element) {
          if (!value || value.trim() === '') return false;
          
          // When no element is provided (like in validateValue), try to detect format from the value itself
          let format = 'IN'; // default
          if (element) {
            format = element.getAttribute('data-currency') || 'IN';
          } else {
            // Auto-detect currency format from the value
            if (value.startsWith('$')) format = 'US';
            else if (value.startsWith('€')) format = 'EU';
            else if (value.startsWith('£')) format = 'GB';
            else if (value.startsWith('¥')) format = 'JP';
            else if (value.startsWith('₹')) format = 'INR';
            else if (value.startsWith('Rs.')) format = 'IN';
          }
          
          const config = this.getCurrencyConfig(format);
          
          // Create dynamic pattern based on currency format
          let patternString;
          if (config.grouping === 'western') {
            // Western format: $1,234.56 or €1.234,56
            if (config.decimal === ',') {
              // Euro style: €1.234,56
              patternString = `^\\${config.symbol}${config.space ? '\\s?' : ''}[0-9]{1,3}(\\.[0-9]{3})*(,[0-9]{1,2})?$`;
            } else {
              // US/UK style: $1,234.56
              patternString = `^\\${config.symbol}${config.space ? '\\s?' : ''}[0-9]{1,3}(,[0-9]{3})*(\\.[0-9]{1,2})?$`;
            }
          } else {
            // Indian format: Rs.12,34,567.89
            patternString = `^\\${config.symbol}${config.space ? '\\s?' : ''}[0-9]{1,3}(,[0-9]{2,3})*(\\.[0-9]{1,2})?$`;
          }
          
          const dynamicPattern = new RegExp(patternString);
          const isValid = dynamicPattern.test(value);
          
          // Set dynamic error message
          const exampleAmount = config.grouping === 'western' 
            ? (config.decimal === ',' ? '1.234,56' : '1,234.56')
            : '12,34,567.89';
          this.message = `Please enter a valid currency amount (e.g., ${config.symbol}${config.space ? ' ' : ''}${exampleAmount}).`;
          
          return isValid;
        },
        setupEvents: function (element) {
          // Get currency format from data attribute or default to INR
          const getCurrencyFormat = () => {
            const format = element.getAttribute('data-currency') || 'IN';
            return this.getCurrencyConfig(format);
          };

          element.addEventListener('input', function () {
            const config = getCurrencyFormat();
            
            // Remove currency symbol and spaces first to get raw value
            let rawValue = this.value;
            if (rawValue.startsWith(config.symbol)) {
              rawValue = rawValue.substring(config.symbol.length);
            }
            if (config.space && rawValue.startsWith(' ')) {
              rawValue = rawValue.substring(1);
            }
            
            // Extract only digits and decimal separator
            let value = rawValue.replace(new RegExp(`[^\\d${config.decimal}]`, 'g'), '');

            // If no digits, clear the field
            if (!value || value === config.decimal) {
              this.value = '';
              return;
            }

            // Handle decimal separator
            let parts = value.split(config.decimal);

            // Format the integer part with thousand separators
            if (parts[0]) {
              // Apply thousand separator based on currency format
              if (config.grouping === 'western') {
                // Western format: 1,234,567
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.thousand);
              } else if (config.grouping === 'indian') {
                // Indian format: 12,34,567
                parts[0] = parts[0].replace(/\B(?=(\d{2})+(\d{3})$)/g, config.thousand);
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.thousand);
              }
            }

            // Limit to 2 decimal places
            if (parts.length > 2) {
              parts = [parts[0], parts[1]];
            }

            if (parts[1] && parts[1].length > 2) {
              parts[1] = parts[1].substring(0, 2);
            }

            // Format with currency symbol only if there's actual content
            const formattedValue = parts.join(config.decimal);
            if (formattedValue && formattedValue !== config.decimal) {
              this.value = config.symbol + (config.space ? ' ' : '') + formattedValue;
            } else {
              this.value = '';
            }
          });
        },

        // Currency configuration method
        getCurrencyConfig: function (format) {
          const configs = {
            'IN': { // India - Rupees
              symbol: 'Rs.',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'indian'
            },
            'INR': { // India - Rupees (alternative)
              symbol: '₹',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'indian'
            },
            'US': { // United States - Dollar
              symbol: '$',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'USD': { // US Dollar (alternative)
              symbol: '$',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'EU': { // Euro
              symbol: '€',
              space: true,
              decimal: ',',
              thousand: '.',
              grouping: 'western'
            },
            'EUR': { // Euro (alternative)
              symbol: '€',
              space: true,
              decimal: ',',
              thousand: '.',
              grouping: 'western'
            },
            'GB': { // Great Britain - Pound
              symbol: '£',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'GBP': { // British Pound (alternative)
              symbol: '£',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'JP': { // Japan - Yen
              symbol: '¥',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'JPY': { // Japanese Yen (alternative)
              symbol: '¥',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'CN': { // China - Yuan
              symbol: '¥',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'CNY': { // Chinese Yuan (alternative)
              symbol: '¥',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'RU': { // Russia - Ruble
              symbol: '₽',
              space: true,
              decimal: ',',
              thousand: ' ',
              grouping: 'western'
            },
            'RUB': { // Russian Ruble (alternative)
              symbol: '₽',
              space: true,
              decimal: ',',
              thousand: ' ',
              grouping: 'western'
            },
            'KR': { // South Korea - Won
              symbol: '₩',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            },
            'KRW': { // Korean Won (alternative)
              symbol: '₩',
              space: false,
              decimal: '.',
              thousand: ',',
              grouping: 'western'
            }
          };

          return configs[format] || configs['IN']; // Default to Indian Rupees
        }
      },

      'validate-social-security': {
        pattern: /^\d{3}-\d{2}-\d{4}$/,
        message: 'Please enter a valid Social Security Number (XXX-XX-XXXX).',
        filterPattern: /[^\d-]/g,
        maxLength: 11,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            let value = this.value.replace(/[^\d]/g, '');
            if (value.length >= 3) {
              value = value.substring(0, 3) + '-' + value.substring(3);
            }
            if (value.length >= 6) {
              value = value.substring(0, 6) + '-' + value.substring(6, 10);
            }
            this.value = value;
          });
        }
      },

      'validate-license-plate': {
        pattern: /^[A-Z0-9]{1,10}$/,
        message: 'Please enter a valid license plate number(AS01BS8888).',
        filterPattern: /[^A-Z0-9]/g,
        maxLength: 10,
        setupEvents: function (element) {
          element.addEventListener('input', function () {
            this.value = this.value.toUpperCase();
          });
        }
      }
    };


    if (customRules && typeof customRules === 'object') {
      this.validationRules = { ...this.validationRules, ...customRules };
    }


    if (typeof document !== 'undefined') {
      this.init();
    }
  }

  /**
   * Initialize validation on forms
   * @public
   */
  init() {
    this.startValidation(this.formClass);
    return this;
  }

  /**
   * Find all forms and set up validation
   * @param {string} formClass - CSS class to identify forms
   * @private
   */
  startValidation(formClass) {
    if (typeof document === 'undefined') return;

    const forms = document.getElementsByClassName(formClass);
    if (forms.length === 0) {
      return;
    }

    Array.from(forms).forEach((form) => {
      this.validateInputs(form);
    });
  }

  /**
   * Set up validation for inputs within a form
   * @param {HTMLElement} form - Form element to validate
   * @private
   */
  validateInputs(form) {
    if (typeof document === 'undefined') return;

    const inputs = form.querySelectorAll('[class*="validate-"]');

    inputs.forEach(input => {

      let errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
      if (!errorSpan) {
        errorSpan = document.createElement('span');
        errorSpan.className = 'text-danger';
        errorSpan.id = `error-message-${input.name || input.id}`;

        const parentDiv = input.closest('div');
        if (parentDiv) {
          parentDiv.insertAdjacentElement('afterend', errorSpan);
        }
      }


      const validationTypes = Array.from(input.classList)
        .filter(className => className.startsWith('validate-'));

      if (validationTypes.length > 0) {

        const validationType = validationTypes[0];
        const rule = this.validationRules[validationType];

        if (rule && typeof rule.setupEvents === 'function') {

          rule.setupEvents(input);
        }

        input.addEventListener('input', (e) => {
          const validationType = validationTypes[0];
          const rule = this.validationRules[validationType];

          if (rule) {
            // Skip text filtering for rules that only have custom validation (like date rules)
            if (rule.filterPattern) {
              const filteredValue = e.target.value.replace(rule.filterPattern, '');


              if (rule.maxLength && filteredValue.length > rule.maxLength) {
                e.target.value = filteredValue.slice(0, rule.maxLength);
              } else {
                e.target.value = filteredValue;
              }
            }


            if (validationType === 'validate-pan' || validationType === 'validate-gst' ||
              validationType === 'validate-udyam' || validationType === 'validate-uam') {
              e.target.value = e.target.value.toUpperCase();
            }


            const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);

            if (e.target.value.trim() === '') {
              errorSpan.textContent = '';
              input.style.borderColor = '';
            } else {
              // Handle async validation
              if (rule.asyncValidation && typeof rule.asyncValidation === 'function') {
                this._handleAsyncValidation(input, rule, errorSpan);
              } else {
                // Use the same validation logic as _validateSingleField
                let isValid = true;

                if (rule.customValidation && typeof rule.customValidation === 'function') {
                  isValid = rule.customValidation.call(rule, e.target.value, e.target);
                } else if (rule.pattern) {
                  isValid = rule.pattern.test(e.target.value);
                }

                if (!isValid) {
                  errorSpan.textContent = rule.message;
                  input.style.borderColor = 'red';
                } else {
                  errorSpan.textContent = '';
                  input.style.borderColor = '';
                }
              }
            }
          }
        });
      }
    });
  }

  /**
   * Validates a specific input field
   * @param {HTMLElement} input - Input element to validate
   * @returns {boolean} - Whether the input is valid
   * @public
   */
  validateField(input) {
    // Simply delegate to the main validation method, validating a single field
    return this._validateSingleField(input);
  }

  /**
   * Internal method to validate a single field
   * @param {HTMLElement} input - Input element to validate
   * @returns {boolean|Promise<boolean>} - Whether the input is valid (or Promise for async validation)
   * @private
   */
  _validateSingleField(input) {
    if (!input || !input.classList) return true;

    // Skip validation if field is disabled
    if (input.hasAttribute('data-validation-disabled')) return true;

    const validationTypes = Array.from(input.classList)
      .filter(className => className.startsWith('validate-'));

    if (validationTypes.length === 0) return true;

    const validationType = validationTypes[0];
    const rule = this.validationRules[validationType];

    // Ensure error span exists
    let errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
    if (!errorSpan) {
      errorSpan = document.createElement('span');
      errorSpan.className = 'text-danger';
      errorSpan.id = `error-message-${input.name || input.id}`;

      const parentDiv = input.closest('div');
      if (parentDiv) {
        parentDiv.insertAdjacentElement('afterend', errorSpan);
      }
    }

    // Clear existing error state
    if (errorSpan) {
      errorSpan.textContent = '';
      input.style.borderColor = '';
    }

    // Skip validation if no rule or empty value
    if (!rule || input.value.trim() === '') {
      return true;
    }

    // Handle async validation
    if (rule.asyncValidation && typeof rule.asyncValidation === 'function') {
      return this._handleAsyncValidation(input, rule, errorSpan);
    }

    let isValid = true;

    // Apply validation logic
    if (rule.onValidate && typeof rule.onValidate === 'function') {
      isValid = rule.onValidate(input);
    } else if (rule.customValidation && typeof rule.customValidation === 'function') {
      isValid = rule.customValidation.call(rule, input.value, input);
    } else if (rule.pattern) {
      isValid = rule.pattern.test(input.value);
    }

    // Display error if validation failed
    if (!isValid && errorSpan) {
      errorSpan.textContent = rule.message;
      input.style.borderColor = 'red';
    }

    return isValid;
  }

  /**
   * Handle async validation for a field
   * @param {HTMLElement} input - Input element to validate
   * @param {Object} rule - Validation rule
   * @param {HTMLElement} errorSpan - Error display element
   * @returns {Promise<boolean>} - Promise resolving to validation result
   * @private
   */
  async _handleAsyncValidation(input, rule, errorSpan) {
    const fieldId = input.name || input.id;
    const value = input.value.trim();

    // Show loading state
    if (errorSpan) {
      errorSpan.textContent = rule.loadingMessage || 'Validating...';
      errorSpan.style.color = '#007bff';
      input.style.borderColor = '#007bff';
    }

    // Add loading class to input
    input.classList.add('async-validating');

    try {
      // Call async validation function
      const result = await rule.asyncValidation.call(rule, value, input);
      
      // Remove loading state
      input.classList.remove('async-validating');

      if (result && typeof result === 'object') {
        // Handle detailed result object
        const isValid = result.isValid;
        const message = result.message || rule.message;

        if (errorSpan) {
          if (isValid) {
            errorSpan.textContent = '';
            errorSpan.style.color = '';
            input.style.borderColor = '';
          } else {
            errorSpan.textContent = message;
            errorSpan.style.color = '#dc3545';
            input.style.borderColor = 'red';
          }
        }

        return isValid;
      } else {
        // Handle boolean result
        const isValid = Boolean(result);

        if (errorSpan) {
          if (isValid) {
            errorSpan.textContent = '';
            errorSpan.style.color = '';
            input.style.borderColor = '';
          } else {
            errorSpan.textContent = rule.message;
            errorSpan.style.color = '#dc3545';
            input.style.borderColor = 'red';
          }
        }

        return isValid;
      }
    } catch (error) {
      // Handle validation error
      input.classList.remove('async-validating');
      
      if (errorSpan) {
        errorSpan.textContent = rule.errorMessage || 'Validation failed. Please try again.';
        errorSpan.style.color = '#dc3545';
        input.style.borderColor = 'red';
      }

      console.error('Async validation error:', error);
      return false;
    }
  }

  /**
   * Validates all inputs and returns validity status
   * @returns {boolean|Promise<boolean>} - True if all inputs are valid, false otherwise (Promise for async validation)
   * @public
   */
  nemoValidateInputs() {
    if (typeof document === 'undefined') return true;

    const forms = document.getElementsByClassName(this.formClass);
    const validationPromises = [];
    let hasErrors = false;

    Array.from(forms).forEach(form => {
      const inputs = form.querySelectorAll('[class*="validate-"]');

      // Clear existing errors first
      inputs.forEach(input => {
        const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
        if (errorSpan) {
          errorSpan.textContent = '';
          input.style.borderColor = '';
        }
      });

      // Validate each input
      inputs.forEach(input => {
        const result = this._validateSingleField(input);
        
        if (result instanceof Promise) {
          // Handle async validation
          validationPromises.push(
            result.then(isValid => {
              if (!isValid) hasErrors = true;
              return isValid;
            })
          );
        } else {
          // Handle sync validation
          if (!result) {
            hasErrors = true;
          }
        }
      });
    });

    // If there are async validations, return a promise
    if (validationPromises.length > 0) {
      return Promise.all(validationPromises).then(() => !hasErrors);
    }

    return !hasErrors;
  }

  /**
   * Validates all inputs asynchronously and returns validity status
   * @returns {Promise<boolean>} - Promise resolving to true if all inputs are valid
   * @public
   */
  async validateInputsAsync() {
    if (typeof document === 'undefined') return true;

    const forms = document.getElementsByClassName(this.formClass);
    const validationPromises = [];

    Array.from(forms).forEach(form => {
      const inputs = form.querySelectorAll('[class*="validate-"]');

      // Clear existing errors first
      inputs.forEach(input => {
        const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
        if (errorSpan) {
          errorSpan.textContent = '';
          input.style.borderColor = '';
        }
      });

      // Validate each input
      inputs.forEach(input => {
        const result = this._validateSingleField(input);
        
        if (result instanceof Promise) {
          validationPromises.push(result);
        } else {
          validationPromises.push(Promise.resolve(result));
        }
      });
    });

    const results = await Promise.all(validationPromises);
    return results.every(result => result === true);
  }

  /**
   * Add a custom validation rule
   * @param {string} name - Rule name (e.g., 'validate-custom')
   * @param {Object} rule - Rule definition
   * @param {RegExp} rule.pattern - Pattern to test against
   * @param {string} rule.message - Error message
   * @param {RegExp} rule.filterPattern - Pattern to filter input in real-time
   * @param {number} [rule.maxLength] - Maximum length for input
   * @public
   */
  addRule(name, rule) {
    if (!name.startsWith('validate-')) {
      name = `validate-${name}`;
    }

    this.validationRules[name] = rule;
    return this;
  }

  /**
   * Remove a validation rule
   * @param {string} name - Rule name to remove
   * @public
   */
  removeRule(name) {
    if (!name.startsWith('validate-')) {
      name = `validate-${name}`;
    }

    delete this.validationRules[name];
    return this;
  }

  /**
   * Get all validation rules
   * @returns {Object} - All validation rules
   * @public
   */
  getRules() {
    return { ...this.validationRules };
  }

  /**
   * Get a specific validation rule
   * @param {string} name - Rule name to get
   * @returns {Object|null} - The validation rule or null if not found
   * @public
   */
  getRule(name) {
    if (!name.startsWith('validate-')) {
      name = `validate-${name}`;
    }
    return this.validationRules[name] || null;
  }

  /**
   * Check if a rule exists
   * @param {string} name - Rule name to check
   * @returns {boolean} - Whether the rule exists
   * @public
   */
  hasRule(name) {
    if (!name.startsWith('validate-')) {
      name = `validate-${name}`;
    }
    return name in this.validationRules;
  }

  /**
   * Validate a value against a specific rule without DOM interaction
   * @param {string} ruleName - Name of the rule to validate against
   * @param {string} value - Value to validate
   * @returns {Object|Promise<Object>} - Validation result with isValid and message properties
   * @public
   */
  validateValue(ruleName, value) {
    if (!ruleName.startsWith('validate-')) {
      ruleName = `validate-${ruleName}`;
    }

    const rule = this.validationRules[ruleName];
    if (!rule) {
      return { isValid: false, message: 'Validation rule not found' };
    }

    if (!value || value.trim() === '') {
      return { isValid: true, message: '' };
    }

    // Handle async validation
    if (rule.asyncValidation && typeof rule.asyncValidation === 'function') {
      return rule.asyncValidation.call(rule, value).then(result => {
        if (result && typeof result === 'object') {
          return {
            isValid: result.isValid,
            message: result.isValid ? '' : (result.message || rule.message)
          };
        } else {
          const isValid = Boolean(result);
          return {
            isValid,
            message: isValid ? '' : rule.message
          };
        }
      }).catch(error => {
        console.error('Async validation error:', error);
        return {
          isValid: false,
          message: rule.errorMessage || 'Validation failed. Please try again.'
        };
      });
    }

    let isValid = true;
    let message = rule.message;

    if (rule.customValidation && typeof rule.customValidation === 'function') {
      isValid = rule.customValidation.call(rule, value, null);
      message = rule.message;
    } else if (rule.pattern) {
      isValid = rule.pattern.test(value);
    }

    return { isValid, message: isValid ? '' : message };
  }

  /**
   * Validate a value against a specific rule asynchronously
   * @param {string} ruleName - Name of the rule to validate against
   * @param {string} value - Value to validate
   * @returns {Promise<Object>} - Promise resolving to validation result
   * @public
   */
  async validateValueAsync(ruleName, value) {
    const result = this.validateValue(ruleName, value);
    
    if (result instanceof Promise) {
      return await result;
    }
    
    return result;
  }

  /**
   * Get validation errors for all fields in the form
   * @returns {Array|Promise<Array>} - Array of error objects with field and message properties
   * @public
   */
  getValidationErrors() {
    if (typeof document === 'undefined') return [];

    const errors = [];
    const asyncValidations = [];
    const forms = document.getElementsByClassName(this.formClass);

    Array.from(forms).forEach(form => {
      const inputs = form.querySelectorAll('[class*="validate-"]');

      inputs.forEach(input => {
        const validationTypes = Array.from(input.classList)
          .filter(className => className.startsWith('validate-'));

        if (validationTypes.length > 0) {
          const validationType = validationTypes[0];
          const result = this.validateValue(validationType, input.value);

          if (result instanceof Promise) {
            // Handle async validation
            asyncValidations.push(
              result.then(asyncResult => {
                if (!asyncResult.isValid) {
                  return {
                    field: input.name || input.id || 'unknown',
                    element: input,
                    rule: validationType,
                    message: asyncResult.message,
                    value: input.value
                  };
                }
                return null;
              })
            );
          } else {
            // Handle sync validation
            if (!result.isValid) {
              errors.push({
                field: input.name || input.id || 'unknown',
                element: input,
                rule: validationType,
                message: result.message,
                value: input.value
              });
            }
          }
        }
      });
    });

    // If there are async validations, return a promise
    if (asyncValidations.length > 0) {
      return Promise.all(asyncValidations).then(asyncErrors => {
        const validAsyncErrors = asyncErrors.filter(error => error !== null);
        return [...errors, ...validAsyncErrors];
      });
    }

    return errors;
  }

  /**
   * Get validation errors for all fields in the form asynchronously
   * @returns {Promise<Array>} - Promise resolving to array of error objects
   * @public
   */
  async getValidationErrorsAsync() {
    if (typeof document === 'undefined') return [];

    const errors = [];
    const forms = document.getElementsByClassName(this.formClass);

    for (const form of forms) {
      const inputs = form.querySelectorAll('[class*="validate-"]');

      for (const input of inputs) {
        const validationTypes = Array.from(input.classList)
          .filter(className => className.startsWith('validate-'));

        if (validationTypes.length > 0) {
          const validationType = validationTypes[0];
          const result = await this.validateValueAsync(validationType, input.value);

          if (!result.isValid) {
            errors.push({
              field: input.name || input.id || 'unknown',
              element: input,
              rule: validationType,
              message: result.message,
              value: input.value
            });
          }
        }
      }
    }

    return errors;
  }

  /**
   * Clear all validation errors and styling
   * @public
   */
  clearValidationErrors() {
    if (typeof document === 'undefined') return;

    const forms = document.getElementsByClassName(this.formClass);

    Array.from(forms).forEach(form => {
      const inputs = form.querySelectorAll('[class*="validate-"]');

      inputs.forEach(input => {
        const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
        if (errorSpan) {
          errorSpan.textContent = '';
        }
        input.style.borderColor = '';
      });
    });

    return this;
  }

  /**
   * Enable or disable validation for specific fields
   * @param {string|Array} fieldNames - Field name(s) to enable/disable
   * @param {boolean} enabled - Whether to enable (true) or disable (false) validation
   * @public
   */
  toggleFieldValidation(fieldNames, enabled = true) {
    if (typeof document === 'undefined') return this;

    const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
    const forms = document.getElementsByClassName(this.formClass);

    Array.from(forms).forEach(form => {
      names.forEach(name => {
        const input = form.querySelector(`[name="${name}"], #${name}`);
        if (input) {
          if (enabled) {
            input.removeAttribute('data-validation-disabled');
            // Trigger validation immediately if field has content
            if (input.value.trim() !== '') {
              this.validateField(input);
            }
          } else {
            input.setAttribute('data-validation-disabled', 'true');
            // Clear any existing errors
            const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
            if (errorSpan) {
              errorSpan.textContent = '';
            }
            input.style.borderColor = '';
          }
        }
      });
    });

    return this;
  }

  /**
   * Set custom error message for a specific field
   * @param {string} fieldName - Field name or ID
   * @param {string} message - Custom error message
   * @public
   */
  setFieldError(fieldName, message) {
    if (typeof document === 'undefined') return this;

    const forms = document.getElementsByClassName(this.formClass);

    Array.from(forms).forEach(form => {
      const input = form.querySelector(`[name="${fieldName}"], #${fieldName}`);
      if (input) {
        const errorSpan = document.getElementById(`error-message-${input.name || input.id}`);
        if (errorSpan) {
          errorSpan.textContent = message;
          input.style.borderColor = message ? 'red' : '';
        }
      }
    });

    return this;
  }

  /**
   * Get form data with validation status
   * @returns {Object|Promise<Object>} - Object containing formData and validation status
   * @public
   */
  getFormData() {
    if (typeof document === 'undefined') return { formData: {}, isValid: true, errors: [] };

    const formData = {};
    const forms = document.getElementsByClassName(this.formClass);

    Array.from(forms).forEach(form => {
      const inputs = form.querySelectorAll('input, select, textarea');

      inputs.forEach(input => {
        const name = input.name || input.id;
        if (name) {
          if (input.type === 'checkbox') {
            formData[name] = input.checked;
          } else if (input.type === 'radio') {
            if (input.checked) {
              formData[name] = input.value;
            }
          } else {
            formData[name] = input.value;
          }
        }
      });
    });

    const errors = this.getValidationErrors();

    if (errors instanceof Promise) {
      return errors.then(resolvedErrors => ({
        formData,
        isValid: resolvedErrors.length === 0,
        errors: resolvedErrors
      }));
    }

    return {
      formData,
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get form data with validation status asynchronously
   * @returns {Promise<Object>} - Promise resolving to object containing formData and validation status
   * @public
   */
  async getFormDataAsync() {
    if (typeof document === 'undefined') return { formData: {}, isValid: true, errors: [] };

    const formData = {};
    const forms = document.getElementsByClassName(this.formClass);

    Array.from(forms).forEach(form => {
      const inputs = form.querySelectorAll('input, select, textarea');

      inputs.forEach(input => {
        const name = input.name || input.id;
        if (name) {
          if (input.type === 'checkbox') {
            formData[name] = input.checked;
          } else if (input.type === 'radio') {
            if (input.checked) {
              formData[name] = input.value;
            }
          } else {
            formData[name] = input.value;
          }
        }
      });
    });

    const errors = await this.getValidationErrorsAsync();

    return {
      formData,
      isValid: errors.length === 0,
      errors
    };
  }
}


if (typeof module !== 'undefined' && module.exports) {
  module.exports = NemoValidator;
} else if (typeof window !== 'undefined') {
  window.NemoValidator = NemoValidator;
}

export default NemoValidator;
